import { useState, useEffect } from "react";
import { Tooltip, Modal, Input, Button } from "antd";
import { GrNote } from "react-icons/gr";
import "./topsideNote.css";
import { IServiceURL } from "../../interfaces/serviceURL.interface";
import { findPriceChangeURL } from "../../utils/utils";
import appConfig from "../../config/config.json";
import {
  getEffectiveDates,
  updateTopsideNote,
} from "../../utils/api/pricechange.service";
import { getEndpoints } from "../../utils/api/config.service";
import { useOktaAuth } from "@okta/okta-react";
import { useSelector } from "react-redux";
import { RootState } from "../../reduxStore/reducers/rootReducer";

const { TextArea } = Input;

const TopsideNote = () => {
  const { authState } = useOktaAuth();
  const readOnlyState = useSelector((state: RootState) => state.readOnly);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serviceURLs, setServiceURLs] = useState<IServiceURL[]>([]);
  const [latestBatchId, setLatestBatchId] = useState<number | null>(null);
  const [noteText, setNoteText] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState(false);

  const handleButtonClick = () => {
    setIsModalOpen(true);
    setIsEditMode(false);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleSave = async () => {
    if (!latestBatchId) {
      console.error("No batch ID available");
      return;
    }

    try {
      const priceChangeAPI = findPriceChangeURL(serviceURLs, appConfig.stage);
      if (!priceChangeAPI) {
        throw new Error("Could not find Price Change API.");
      }

      const body = {
        note: noteText,
      };

      await updateTopsideNote(priceChangeAPI, body, latestBatchId);
      setIsEditMode(false);
    } catch (err) {
      console.error("Failed to update topside note:", err);
    }
  };

  const handleCancel = () => {
    // Reload the note from the database when canceling edit
    if (isEditMode) {
      fetchLatestBatchId();
      setIsEditMode(false);
    } else {
      setIsModalOpen(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
  };

  const fetchServiceURLs = async () => {
    try {
      const response = (await getEndpoints()) as any;
      const data = await JSON.parse(response);
      setServiceURLs(data["endpoints"]);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchServiceURLs();
  }, [authState]);

  const fetchLatestBatchId = async () => {
    if (!serviceURLs.length) return;

    try {
      const priceChangeAPI = findPriceChangeURL(serviceURLs, appConfig.stage);
      if (!priceChangeAPI) {
        throw new Error("Could not find Price Change API.");
      }

      const response = (await getEffectiveDates(priceChangeAPI)) as any;
      const data = await response.json();

      if (data && data.length > 0) {
        const latestBatch = data[data.length - 1];
        setLatestBatchId(latestBatch.id);
        setNoteText(latestBatch.note || "");
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (serviceURLs.length > 0) {
      fetchLatestBatchId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceURLs]);

  return (
    <div className="note-container">
      <Tooltip placement="left" title="Topside Note">
        <button onClick={handleButtonClick} className="topside-note-button">
          <GrNote size={20} color={"white"} />
        </button>
      </Tooltip>
      <Modal
        title="Topside Note"
        open={isModalOpen}
        onCancel={handleClose}
        footer={
          isEditMode
            ? [
                <Button key="cancel" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                  Save
                </Button>,
              ]
            : [
                <Button key="close" onClick={handleClose}>
                  Close
                </Button>,
                ...(readOnlyState.isReadOnly
                  ? []
                  : [
                      <Button key="edit" type="primary" onClick={handleEdit}>
                        Edit
                      </Button>,
                    ]),
              ]
        }
      >
        {isEditMode ? (
          <TextArea
            rows={4}
            placeholder="Enter topside note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        ) : (
          <div style={{ minHeight: "100px", whiteSpace: "pre-wrap" }}>
            {noteText || ""}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TopsideNote;
